import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

const getModalElement = async (modalId) => {
  const existingModal = document.getElementById(modalId);
  if (
    existingModal &&
    (typeof existingModal.showOverlay === "function" ||
      typeof existingModal.hideOverlay === "function")
  ) {
    return existingModal;
  }

  if (typeof customElements !== "undefined" && !customElements.get("s-modal")) {
    await Promise.race([
      customElements.whenDefined("s-modal"),
      new Promise((resolve) => {
        setTimeout(resolve, 1000);
      }),
    ]);
  }

  return document.getElementById(modalId);
};

const normalizeActions = (actions) => {
  if (!actions) {
    return [];
  }
  return Array.isArray(actions) ? actions : [actions];
};

const getModalDialogElement = (modal) => {
  if (!modal?.shadowRoot) {
    return null;
  }

  return modal.shadowRoot.querySelector("dialog");
};

const isModalOverlayVisible = (modal) => {
  const dialog = getModalDialogElement(modal);
  return Boolean(dialog?.open);
};

const hasOtherVisibleModals = (exceptModalId) =>
  Array.from(document.querySelectorAll("s-modal")).some(
    (el) => el.id !== exceptModalId && isModalOverlayVisible(el)
  );

const waitUntilNoOtherModalsVisible = (exceptModalId, maxWaitMs = 600) =>
  new Promise((resolve) => {
    if (!hasOtherVisibleModals(exceptModalId)) {
      resolve();
      return;
    }

    let resolved = false;
    const deadline = Date.now() + maxWaitMs;
    const otherModals = Array.from(document.querySelectorAll("s-modal")).filter(
      (el) => el.id !== exceptModalId
    );

    const finish = () => {
      if (resolved) {
        return;
      }
      resolved = true;
      otherModals.forEach((modal) => {
        modal.removeEventListener("hide", onOtherHide);
      });
      resolve();
    };

    const onOtherHide = (event) => {
      if (event.target?.localName !== "s-modal") {
        return;
      }
      if (!hasOtherVisibleModals(exceptModalId)) {
        requestAnimationFrame(() => requestAnimationFrame(finish));
      }
    };

    otherModals.forEach((modal) => {
      modal.addEventListener("hide", onOtherHide);
    });

    const poll = () => {
      if (!hasOtherVisibleModals(exceptModalId)) {
        requestAnimationFrame(() => requestAnimationFrame(finish));
        return;
      }
      if (Date.now() >= deadline) {
        finish();
        return;
      }
      requestAnimationFrame(poll);
    };

    requestAnimationFrame(poll);
    setTimeout(finish, maxWaitMs);
  });

/**
 * Polaris Modal compatible wrapper around Shopify App Home s-modal.
 *
 * Dropdowns inside the modal must use s-popover/s-tooltip with commandFor.
 * Polaris Popover/Tooltip/Autocomplete portal into document.body, which sits
 * below the modal's top layer, and a plain absolutely positioned list gets
 * clipped by the shadow-DOM scroll box wrapping the content slot.
 *
 * s-popover positions itself from its activator's bounding rect, so the
 * activator must stay mounted for as long as the popover can be open.
 */
const ShopifyModal = forwardRef(
  (
    {
      id,
      open = false,
      onClose,
      heading,
      title,
      size = "base",
      padding = "base",
      accessibilityLabel,
      primaryAction,
      secondaryActions,
      showCloseButton = true,
      closeText = "Close",
      children,
    },
    ref
  ) => {
    const generatedId = useId().replace(/:/g, "");
    const modalId = id || `shopify-modal-${generatedId}`;
    const modalRef = useRef(null);
    const suppressCloseSideEffects = useRef(false);
    const modalWasVisibleRef = useRef(false);
    const closedByUserRef = useRef(false);
    const isHandlingCloseRef = useRef(false);
    const onCloseRef = useRef(onClose);
    const openRef = useRef(open);

    onCloseRef.current = onClose;
    openRef.current = open;

    useImperativeHandle(ref, () => ({
      hideOverlay: () => {
        if (typeof modalRef.current?.hideOverlay === "function") {
          modalRef.current.hideOverlay();
        }
      },
      showOverlay: () => {
        if (typeof modalRef.current?.showOverlay === "function") {
          modalRef.current.showOverlay();
        }
      },
      getElement: () => modalRef.current,
    }));

    useEffect(() => {
      let isActive = true;
      let modalElement = null;

      // Once `hide` is dispatched the dialog is already closing, so reverting
      // with showOverlay() here would desync the React `open` prop from the real
      // dialog state and leave an open overlay with unmounted children. Unwanted
      // dismissals must be blocked before they reach s-modal, not undone after.
      const handleHide = (event) => {
        // s-popover/s-tooltip dispatch `hide` with bubbles:true, so a nested
        // overlay closing reaches this listener and would close the modal.
        // s-modal's own `hide` does not bubble and targets the modal itself.
        if (event.target !== modalElement) {
          return;
        }
        if (suppressCloseSideEffects.current) {
          suppressCloseSideEffects.current = false;
          return;
        }
        if (isHandlingCloseRef.current) {
          return;
        }
        isHandlingCloseRef.current = true;
        closedByUserRef.current = true;
        onCloseRef.current?.();
        isHandlingCloseRef.current = false;
      };

      getModalElement(modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("hide", handleHide);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("hide", handleHide);
      };
    }, [modalId]);

    // s-modal dismisses itself from a bubble-phase click listener on its shadow
    // <dialog>, guarded by `event.target === dialog` — which is true both for
    // the backdrop and for the panel's own padding. Intercepting in the capture
    // phase on the host runs before anything inside the shadow tree, so
    // hideOverlay() is never reached and no `hide` event is dispatched. Escape
    // is a separate keydown listener and stays functional.
    useEffect(() => {
      if (!open) {
        return undefined;
      }

      let isActive = true;
      let modalElement = null;

      const blockBackdropDismiss = (event) => {
        const dialog = getModalDialogElement(modalElement);
        if (dialog && event.composedPath()[0] === dialog) {
          event.stopImmediatePropagation();
        }
      };

      getModalElement(modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("click", blockBackdropDismiss, true);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("click", blockBackdropDismiss, true);
      };
    }, [open, modalId]);

    useEffect(() => {
      let isActive = true;

      if (!open && modalWasVisibleRef.current) {
        suppressCloseSideEffects.current = !closedByUserRef.current;
      }

      getModalElement(modalId).then((modal) => {
        if (!modal || !isActive) {
          return;
        }

        if (open) {
          modalWasVisibleRef.current = true;
          waitUntilNoOtherModalsVisible(modalId).then(() => {
            if (!isActive || !openRef.current) {
              return;
            }
            if (typeof modal.showOverlay === "function") {
              modal.showOverlay();
            }
          });
        } else if (modalWasVisibleRef.current) {
          if (closedByUserRef.current) {
            closedByUserRef.current = false;
            return;
          }
          if (typeof modal.hideOverlay === "function") {
            modal.hideOverlay();
          }
        }
      });

      return () => {
        isActive = false;
      };
    }, [open, modalId]);

    const secondaryActionList = normalizeActions(secondaryActions);
    const shouldRenderDefaultClose =
      showCloseButton && secondaryActionList.length === 0;

    const modalHeading = heading || title;

    return (
      <s-modal
        id={modalId}
        ref={modalRef}
        heading={modalHeading}
        size={size}
        padding={padding}
        accessibilityLabel={accessibilityLabel}
      >
        {open ? (
          <div className="shopify-modal-content-shell">{children}</div>
        ) : null}

        {secondaryActionList.map((action, index) => {
          const { content, onAction, disabled, loading, tone = "auto" } = action;

          if (onAction) {
            return (
              <s-button
                key={`secondary-${index}-${content}`}
                slot="secondary-actions"
                variant="secondary"
                tone={tone}
                disabled={disabled || undefined}
                loading={loading || undefined}
                onClick={onAction}
              >
                {content}
              </s-button>
            );
          }

          return (
            <s-button
              key={`secondary-${index}-${content}`}
              slot="secondary-actions"
              variant="secondary"
              tone={tone}
              disabled={disabled || undefined}
              loading={loading || undefined}
              commandFor={modalId}
              command="--hide"
            >
              {content}
            </s-button>
          );
        })}

        {shouldRenderDefaultClose && (
          <s-button
            slot="secondary-actions"
            commandFor={modalId}
            command="--hide"
          >
            {closeText}
          </s-button>
        )}

        {primaryAction && (
          <s-button
            slot="primary-action"
            variant="primary"
            tone={primaryAction.destructive ? "critical" : primaryAction.tone || "auto"}
            disabled={primaryAction.disabled || undefined}
            loading={primaryAction.loading || undefined}
            onClick={primaryAction.onAction}
          >
            {primaryAction.content}
          </s-button>
        )}
      </s-modal>
    );
  }
);

ShopifyModal.displayName = "ShopifyModal";

export default ShopifyModal;
